import React, { useCallback, useState, useEffect } from 'react';
import {
  DocumentArrowDownIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import reportService from '../../services/reportService';
import agencyService from '../../services/agencyService';
import { useToast } from '../../contexts/ToastContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { format, startOfMonth, endOfMonth } from 'date-fns';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('financial');
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  });
  const [agencyId, setAgencyId] = useState('all');
  const [agencies, setAgencies] = useState([]);
  const [reportData, setReportData] = useState(null);
  const { showError } = useToast();
  const { formatCurrency } = useCurrency();

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        agency_id: agencyId !== 'all' ? agencyId : undefined,
      };

      switch (reportType) {
        case 'financial':
          data = await reportService.getFinancialReport(params);
          break;
        case 'portfolio':
          data = await reportService.getPortfolioReport(params);
          break;
        case 'agency-performance':
          data = await reportService.getAgencyPerformance(params);
          break;
        case 'loan-products':
          data = await reportService.getLoanProductReport(params);
          break;
        case 'clients':
          data = await reportService.getClientReport(params);
          break;
        default:
          data = await reportService.getFinancialReport(params);
      }

      setReportData(data);
    } catch (error) {
      showError('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  }, [agencyId, dateRange.end, dateRange.start, reportType, showError]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const data = await agencyService.getAllAgencies();
        setAgencies(Array.isArray(data) ? data : data.agencies || []);
      } catch (error) {
        setAgencies([]);
      }
    };

    fetchAgencies();
  }, []);

  const handleExport = async (format = 'pdf') => {
    try {
      const exportFormat = format === 'excel' ? 'csv' : format;
      const blob = await reportService.exportReport(reportType, exportFormat, {
        start_date: dateRange.start,
        end_date: dateRange.end,
        agency_id: agencyId !== 'all' ? agencyId : undefined,
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_${reportType}_${dateRange.start}_${dateRange.end}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError('Erreur lors de l\'export du rapport');
    }
  };

  const getReportTitle = () => {
    const titles = {
      financial: 'Rapport Financier',
      portfolio: 'Portefeuille de Prêts',
      'agency-performance': 'Performance des Agences',
      'loan-products': 'Performance des Produits de Prêt',
      clients: 'Rapport Clients',
    };
    return titles[reportType] || 'Rapport';
  };

  const renderFinancialReport = () => {
    if (!reportData) return null;
    const revenue = reportData.revenue || {};

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-md bg-green-100 p-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Revenus Totaux</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(reportData.total_revenue || revenue.total)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-md bg-blue-100 p-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Intérêts Perçus</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(reportData.interest_income || revenue.interest)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-md bg-yellow-100 p-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pénalités</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(reportData.penalties || revenue.penalties)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-md bg-red-100 p-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Dépenses</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(reportData.expenses)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Évolution des Revenus</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={reportData.revenue_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Montant']} />
                  <Legend />
                  <Line type="monotone" dataKey="interest" name="Intérêts" stroke="#8884d8" />
                  <Line type="monotone" dataKey="penalties" name="Pénalités" stroke="#82ca9d" />
                  <Line type="monotone" dataKey="fees" name="Frais" stroke="#ffc658" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPortfolioReport = () => {
    if (!reportData) return null;
    const statusDistribution = reportData.loan_status_distribution || reportData.status_distribution || [];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Portfolio Summary */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Portefeuille de Prêts</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Montant Total des Prêts</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(reportData.total_portfolio)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Prêts Actifs</span>
                  <span className="text-sm font-medium text-gray-900">
                    {reportData.active_loans || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Taux de Défaillance</span>
                  <span className="text-sm font-medium text-gray-900">
                    {reportData.default_rate || 0}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Taux de Recouvrement</span>
                  <span className="text-sm font-medium text-gray-900">
                    {reportData.collection_rate || 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Loan Status Distribution */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Répartition par Statut</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {COLORS.map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} prêts`, 'Nombre']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Loan Performance */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Performance des Prêts</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.loan_performance || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="product" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Montant']} />
                  <Legend />
                  <Bar dataKey="disbursed" name="Décaissé" fill="#8884d8" />
                  <Bar dataKey="repaid" name="Remboursé" fill="#82ca9d" />
                  <Bar dataKey="outstanding" name="En cours" fill="#ffc658" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Génération du rapport...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Rapports</h1>
          <p className="mt-1 text-sm text-gray-600">
            Rapports et analyses financières
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => handleExport('pdf')}
            className="btn-secondary"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Exporter PDF
          </button>
          <button
            onClick={() => handleExport('excel')}
            className="btn-primary"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Exporter CSV
          </button>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="card">
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Type de Rapport
              </label>
              <select
                className="input-field mt-1"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                <option value="financial">Financier</option>
                <option value="portfolio">Portefeuille</option>
                <option value="agency-performance">Performance Agences</option>
                <option value="loan-products">Produits de Prêt</option>
                <option value="clients">Clients</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date Début
              </label>
              <input
                type="date"
                className="input-field mt-1"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date Fin
              </label>
              <input
                type="date"
                className="input-field mt-1"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Agence
            </label>
            <select
              className="input-field mt-1"
              value={agencyId}
              onChange={(e) => setAgencyId(e.target.value)}
            >
              <option value="all">Toutes les agences</option>
              {agencies.map((agency) => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Report Title */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {getReportTitle()}
          </h2>
          <p className="text-sm text-gray-600">
            Période: {format(new Date(dateRange.start), 'dd/MM/yyyy')} - {format(new Date(dateRange.end), 'dd/MM/yyyy')}
          </p>
        </div>
      </div>

      {/* Report Content */}
      <div className="space-y-6">
        {reportType === 'financial' && renderFinancialReport()}
        {reportType === 'portfolio' && renderPortfolioReport()}
        {reportType === 'agency-performance' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance par Agence</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData?.agencies || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => [formatCurrency(value), 'Montant']} />
                    <Legend />
                    <Bar dataKey="disbursed" name="Décaissé" fill="#8884d8" />
                    <Bar dataKey="collected" name="Collecté" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
        {reportType === 'loan-products' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance des Produits</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData?.products || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => [formatCurrency(value), 'Montant']} />
                    <Legend />
                    <Bar dataKey="active_loans" name="Prêts Actifs" fill="#8884d8" />
                    <Bar dataKey="total_disbursed" name="Total Décaissé" fill="#82ca9d" />
                    <Bar dataKey="total_repaid" name="Total Remboursé" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
        {reportType === 'clients' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Statistiques Clients</h3>
              {(() => {
                const gender = reportData?.gender_distribution || {};
                const distribution = reportData?.client_distribution || [
                  { name: 'Hommes', value: gender.male || 0 },
                  { name: 'Femmes', value: gender.female || 0 },
                  { name: 'Autres', value: gender.other || 0 },
                ];
                return (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Nouveaux Clients</span>
                    <span className="text-sm font-medium text-gray-900">
                      {reportData?.new_clients || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Clients Actifs</span>
                    <span className="text-sm font-medium text-gray-900">
                      {reportData?.active_clients || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Clients Inactifs</span>
                    <span className="text-sm font-medium text-gray-900">
                      {reportData?.inactive_clients || 0}
                    </span>
                  </div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={distribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {COLORS.map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} clients`, 'Nombre']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
